import { Stars01Icon } from '@repo/studio-design-system-ui-icons/weather/stars-01'
import { Button } from '@repo/studio-design-system-ui-button'
import type { ButtonVariant } from '@repo/studio-design-system-ui-button'
import { useTrialCta } from '@repo/studio-hooks-payments/use-trial-cta'
import 'payments/payments-dialog/styles.css'
import { tw } from 'utils/classnames'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useIsSubscriberQuery } from '@repo/studio-hooks-studio-ai/use-is-subscriber'

interface GoProForFreeButtonProps {
  label?: string
  isFreeTrial?: boolean
  alwaysDisplayText?: boolean
  variant?: ButtonVariant
  className?: string
}

export function GoProForFreeButton(props: GoProForFreeButtonProps) {
  const { data: isSubscriber, isPending } = useIsSubscriberQuery()

  return isPending || isSubscriber === true ? null : <TrialCtaButton {...props} />
}

/**
 * Reading the pricing experiment enrolls the visitor, so this mounts only once the
 * subscriber check has settled: subscribers have no trial to convert.
 */
function TrialCtaButton({
  label,
  isFreeTrial,
  variant = 'primary',
  alwaysDisplayText,
  className,
}: GoProForFreeButtonProps) {
  const t = useStudioTranslate()
  const trialCta = useTrialCta({
    controlLabel: isFreeTrial
      ? t('studio.flow.top_bar.go_pro_free_trial')
      : t('studio.flow.top_bar.go_pro'),
    tracksControlClicks: false,
  })
  const displayLabel = label ?? trialCta.label

  if (alwaysDisplayText) {
    return (
      <Button
        className={className}
        type="button"
        size="sm"
        variant={variant}
        onClick={trialCta.onClick}
        data-testid="go-pro-for-free-button"
      >
        <Stars01Icon />
        {displayLabel}
      </Button>
    )
  }

  return (
    <>
      <Button
        className={tw('lg:hidden', className)}
        type="button"
        size="sm"
        variant={variant}
        iconOnly
        aria-label={displayLabel}
        onClick={trialCta.onClick}
      >
        <Stars01Icon />
      </Button>
      <Button
        className={tw('hidden lg:flex', className)}
        type="button"
        size="sm"
        variant={variant}
        onClick={trialCta.onClick}
        data-testid="go-pro-for-free-button"
      >
        <Stars01Icon />
        {displayLabel}
      </Button>
    </>
  )
}
